import type { BoxProps } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react';
import { Grid, gridConfig } from '@time-line/components/common';
import type { SanityImage } from '@time-line/components/types';
import { BlurredHeroBackgroundImage } from 'components/common/BlurredHeroBackgroundImage';
import { useIsMounted } from 'hooks/useIsMounted';
import { useImage } from 'lib/sanity';
import Img from 'next/image';
import type { ReactNode } from 'react';

const { gridBorderRadius } = gridConfig;

interface ProfileImageSplitProps extends BoxProps {
  image: SanityImage;
  imageMaxWidth?: number;
  children: ReactNode;
}

export function ProfileImageSplit({
  image,
  imageMaxWidth,
  children,
  ...props
}: ProfileImageSplitProps) {
  const isMounted = useIsMounted();
  const imageProps = useImage(image || null, {
    ratio: 1 / 1,
  });

  return (
    <Grid
      className="section--hero"
      withBorder
      position="relative"
      color="white"
      alignItems="center"
      borderRadius={gridBorderRadius}
      overflow="hidden"
      py={{ md: '2xl' }}
    >
      <BlurredHeroBackgroundImage image={image} />
      <Grid.Inner gridRowGap={0}>
        <Box
          className="hero_media"
          gridColumn={{ base: 'span 12', md: '1 / 6' }}
          justifyItems="center"
          alignSelf={{ md: 'center' }}
          px={{ md: 'sm' }}
          pos="relative"
          zIndex={1}
        >
          <Box m="auto" maxW={{ base: 140, md: imageMaxWidth }}>
            {image && imageProps && (
              <Box
                sx={{ img: { borderRadius: 'full' } }}
                pt={{ base: 'm', md: '0' }}
              >
                <Img
                  {...imageProps}
                  sizes="(min-width: 1920px) 380px, (min-width: 960px) 30vw, 140px"
                  style={{
                    objectFit: 'cover',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          position="relative"
          zIndex={1}
          alignSelf="center"
          gridColumn={{
            base: 'span 12',
            md: '6 / span 7',
          }}
          pt={{ base: '2xs', md: 0 }}
          opacity={isMounted ? 1 : 0}
          {...props}
        >
          {children}
        </Box>
      </Grid.Inner>
    </Grid>
  );
}
